import {t} from 'i18next';
import {Common, CoverType, PageConstants} from '@taopix/taopix-core';
import {PageInterface, ProductLayout} from '@taopix/taopix-core/dist/types/types';

/**
 * maps 2 arrays of objects into one
 *
 * @param   {keyof}   key
 * @param   {T[]}     a1
 * @param   {T[]}     a2
 *
 * @return  {T[]}
 */
const mergeObjectArray = <T>(key: keyof T, a1: T[], a2: T[]): T[] => {
  return a1.map(e => {
    const update = a2.find(p => p[key] === e[key]);
    if (!update) {
      return e;
    }
    return mergeObjects(e, update);
  });
};

/**
 * Merge [n] objects into one
 *
 * @param   {T}    o1
 * @param   {K}    o2
 * @param   {K[]}  rest
 *
 * @return  {T}
 */
const mergeObjects = <T, K extends T>(o1: T, o2: K, ...rest: K[]): T => {
  return {
    ...o1,
    ...o2,
    ...rest,
  };
};

type Filter<T> = {
  [key in keyof T]: string | number | FilterCondition;
};

type FilterCondition = {
  'gt | lt | ne': number;
};

/**
 * Used in Array.filter
 *
 * @param   {T}        target
 * @param   {K}        filter
 *
 * @return  {boolean}
 */
const filterObject = <T, K extends Partial<T>>(target: T, filter: Filter<K>): boolean => {
  const keys = Object.keys(filter);
  if (keys.length === 0) {
    return true;
  }

  return keys
    .map(key => {
      if (null === filter[key]) {
        return true;
      }
      if (typeof filter[key] !== 'object') {
        // eslint-disable-next-line eqeqeq
        return target[key as keyof T] == filter[key];
      } else {
        return Object.keys(filter[key])
          .map(k => {
            switch (k) {
              case 'gt': {
                return Number(target[key as keyof T]) > Number(filter[key][k]);
              }
              case 'ne': {
                return Number(target[key as keyof T]) !== Number(filter[key][k]);
              }
              case 'lt': {
                return Number(target[key as keyof T]) < Number(filter[key][k]);
              }
              default: {
                return false;
              }
            }
          })
          .every(value => value);
      }
    })
    .every(value => value);
};

const isCombinedCover = (coverType: CoverType) => {
  return coverType === CoverType.Combined;
};

const getPageName = (
  page: PageInterface,
  layout: ProductLayout,
  capitaliseFirstLetter?: boolean
): string => {
  if ('' !== page.predefinedname) {
    const localString = Common.getCurrentLocaleString(page.predefinedname, true);
    if (capitaliseFirstLetter) {
      return localString.charAt(0).toUpperCase() + localString.slice(1);
    }
    return localString;
  }

  if (PageConstants.FrontCoverPageNumber === page.pagenumber) {
    if (isCombinedCover(layout.cover.cover1type)) {
      return t('str_LabelCover');
    }
    return t('str_LabelFrontCover2');
  } else if (PageConstants.BackCoverPageNumber === page.pagenumber) {
    return t('str_LabelBackCover2');
  }

  return page.name;
};

/**
 * Locates the first scrollable parent of the element provided.
 *
 * @param node On first pass, this is the element to start from. Following calls will be the node's parent.
 * @returns The scrollable element, or null if none found.
 */
const getScrollParent = (node: HTMLElement | null): HTMLElement | null => {
  if (!(node instanceof HTMLElement)) {
    return null;
  }

  const computed = window.getComputedStyle(node);

  if (
    (!['visible', 'hidden'].includes(computed.overflowY) &&
      node.scrollHeight > node.clientHeight) ||
    (!['visible', 'hidden'].includes(computed.overflowX) && node.scrollWidth > node.clientWidth)
  ) {
    return node;
  } else {
    return getScrollParent(node.parentNode as HTMLElement);
  }
};

/**
 * Wait for element scroll
 */
const waitForScroll = (() => {
  let timeout = 0;
  let resolveFunction;
  const onScroll = () => {
    clearTimeout(timeout);
    timeout = window.setTimeout(() => {
      resolveFunction();
    }, 100);
  };

  return {
    scroll(target: HTMLElement | null, options: ScrollIntoViewOptions) {
      return new Promise((resolve, reject) => {
        target?.addEventListener('scroll', onScroll);
        resolveFunction = resolve;
        onScroll();
        target?.scrollIntoView(options);
      }).then(() => {
        target?.removeEventListener('scroll', onScroll);
        resolveFunction = null;
      });
    },
  };
})();

/**
 * Returns an updated url if the original does not begin with https://...
 */
const getUrl = (url: string): string => {
  if (url !== '' && !url.startsWith('https://')) {
    // if it contains http:// just return normal url
    if (url.startsWith('http://')) {
      return url;
    }
    return `https://${url}`;
  }
  return url;
};

/**
 * Appends a string value with the online api url
 *
 * @param   {string}  str
 *
 * @return  {string}
 */
const formatOnlineUrl = (str: string): string => {
  if (!str.startsWith('http')) {
    return `${window.config.baseURL}/${str}`;
  }
  return str;
};

export {
  mergeObjectArray,
  mergeObjects,
  filterObject,
  getPageName,
  getScrollParent,
  waitForScroll,
  getUrl,
  formatOnlineUrl,
};
